import type { LedgerEntry } from "@utopia-well-done-ltd/contracts";
import { useEffect, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { asAmount, asMoment } from "../format.js";
import { WALLET_KIND_LABELS } from "../wallet-kinds.js";

/**
 * The player's money, movement by movement.
 *
 * Entries and not transactions: a settlement has legs this player does not hold — the house paying
 * out — and putting those on the screen would show an operator a figure that is not this player's.
 *
 * Paged with a cursor rather than a page number. The ledger is appended to while it is being read,
 * and a page number would skip or repeat rows as new ones land above the page.
 */
export function LedgerList({ playerId }: { playerId: string }) {
  const [entries, setEntries] = useState<LedgerEntry[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    // Cleared before the fetch, not just on error: switching players must not leave the previous
    // player's money on screen labelled as this one's.
    setEntries(null);
    setCursor(null);
    setError(null);

    api
      .playerLedger(playerId, { signal: controller.signal })
      .then((page) => {
        setEntries(page.entries);
        setCursor(page.nextCursor);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught.message : "Could not load the ledger.");
      });

    return () => controller.abort();
  }, [playerId]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const page = await api.playerLedger(playerId, { before: cursor });
      setEntries((current) => [...(current ?? []), ...page.entries]);
      setCursor(page.nextCursor);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not load more entries.");
    } finally {
      setLoadingMore(false);
    }
  }

  if (error)
    return (
      <p className="banner" role="alert">
        {error}
      </p>
    );
  if (!entries) return <p className="empty">Loading…</p>;
  if (entries.length === 0) return <p className="empty">This account has never moved money.</p>;

  return (
    <>
      <table className="mobile-cards">
        <thead>
          <tr>
            <th scope="col" style={{ width: "20%" }}>
              When
            </th>
            <th scope="col" style={{ width: "18%" }}>
              Movement
            </th>
            <th scope="col" style={{ width: "14%" }}>
              Account
            </th>
            <th scope="col" style={{ textAlign: "right", width: "18%" }}>
              Amount
            </th>
            <th scope="col">Reference</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td className="label-13 dim" data-label="When">
                {asMoment(entry.createdAt)}
              </td>
              <td className="label-13" data-label="Movement">
                {WALLET_KIND_LABELS[entry.kind]}
                {/* A correction is the one row whose meaning depends on another row. */}
                {entry.reversalOf ? <span className="label-13 faint"> · reverses</span> : null}
              </td>
              <td className="label-13 dim" data-label="Account">
                {entry.accountType}
              </td>
              <td className="mono-12" data-label="Amount" style={{ textAlign: "right" }}>
                {asAmount(entry.amount, { sign: true })}
              </td>
              <td
                className="label-13 dim truncate"
                data-label="Reference"
                title={entry.referenceId ?? undefined}
              >
                {entry.referenceId ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {cursor ? (
        <button
          className="btn btn-quiet load-more-button"
          disabled={loadingMore}
          onClick={() => void loadMore()}
          type="button"
        >
          {loadingMore ? "Loading…" : "Load more"}
        </button>
      ) : null}
    </>
  );
}
